import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import ReactGA from 'react-ga'
import debounce from 'lodash/debounce'
import Button from '../../../../../../components/Form/Button'
import useForm from './hooks/useForm'

import { validateEmail } from '../../../../../../utils/functionalitiesHelper'

import * as S from './styles'
import Alert from '../../../../components/Form/Alert'
import { getUserByCpf, getUserByEmail } from '../../../../../../services/user'
import { formatCpf } from '../../../../../../utils/formatters'
import { DEFAULT_VALUES } from './hooks/constants'

const NewTutorForm = ({ tutorData = undefined, visibility = false, setVisibility = undefined, firstStep = [], updated }) => {
  const [loading, setLoading] = useState(false)
  const [tutor, setTutor] = useState()
  const [tutorByCpf, setTutorByCpf] = useState()
  const [isFirstStep, setIsFirstStep] = firstStep
  const history = useHistory()
  const isEditing = !!tutorData
  const id = tutorData?.id || undefined
  const { userId, id: tutorId, firstName, lastName, email, homePhone, document } = tutorData || {}
  const tutorWithoutEmail = !email && isEditing
  const { errors, clearErrors, watch, reset, register, getValues, setValue, setError, submitForm } = useForm({ tutorWithoutEmail })

  const goToPage = (url) => {
    history.push(`/${url}`)
    ReactGA.pageview(`/${url}`)
  }

  useEffect(() => {
    if (isEditing) {
      const name = catchName()
      reset({
        name,
        email,
        ...(document ? { document } : ''),
        ...(homePhone ? { homePhone } : '')
      })
      setIsFirstStep(false)
    }
  }, [isEditing, tutorData])

  useEffect(() => {
    if (!visibility && !isEditing) {
      reset(DEFAULT_VALUES)
      setTutor()
      setTutorByCpf()
      setIsFirstStep(true)
    } else if (!visibility && isEditing) {
      const name = catchName()
      reset({
        name,
        email,
        ...(document ? { document } : ''),
        ...(homePhone ? { homePhone } : '')
      })
    }
  }, [visibility])

  useEffect(() => {
    const pattern = /^\d{11}$/
    if ((tutorData?.document === watch.document)) {
      setTutorByCpf()
    } else if (pattern.test(watch.document)) {
      const debouncedCpf = debounce(() => handleValidateCpf(), 500)
      debouncedCpf()
    } else {
      setTutorByCpf()
      handleInsertManualError({ field: 'document', condition: (!watch.document && tutorData?.email), message: 'Digite um CPF válido' })
    }
  }, [watch.document])

  useEffect(() => {
    handleInsertManualError({ field: 'homePhone', condition: (!watch.homePhone && tutorData?.email), message: 'Digite um telefone válido' })
  }, [watch.homePhone])

  useEffect(() => {
    handleInsertManualError({ field: 'email', condition: (tutor?.id), message: 'Digite um e-mail válido' })
  }, [tutor])

  const handleInsertManualError = ({ field, condition, message = 'Este campo é obrigatório' }) => {
    if (condition) {
      setError(field, {
        type: 'manual',
        message
      })
    } else {
      clearErrors(field)
    }
  }

  const catchName = () => {
    if (lastName) return firstName?.concat(' ', lastName) || ''
    return firstName
  }

  const handleGetTutorByEmail = async () => {
    try {
      setLoading(true)
      const req = await getUserByEmail(watch.email)
      if (req[0] !== '') {
        if (!isEditing) {
          goToPage(`vets/detalhes-do-tutor/${req[0].id}?alertContent=HAS_ACCOUNT`)
        }
      } else {
        setIsFirstStep(false)
      }
      setLoading(false)
    } catch (error) {
    }
  }

  const handleValidateName = () => {
    const splitedName = getValues('name').trim().split(' ')

    handleInsertManualError({ field: 'name', condition: (splitedName.length === 1), message: 'Insira o nome completo do tutor' })
  }

  const handleValidateEmail = ({ callback }) => {
    const email = getValues('email')
    if (validateEmail(email)) {
      clearErrors('email')
      callback()
    } else {
      setError('email', {
        type: 'manual',
        message: 'Digite um e-mail válido'
      })
    }
  }

  const handleValidateCpf = async () => {
    try {
      setLoading(true)
      clearErrors('document')
      const req = await getUserByCpf(watch.document)
      if (req.length) {
        setTutorByCpf(req[0])
      } else {
        setTutorByCpf()
      }
      setLoading(false)
    } catch (error) {
      console.error(error)
      setError('document', {
        type: 'manual',
        message: 'Digite um CPF válido'
      })
      setTutorByCpf()
      setLoading(false)
    }
  }

  const handleContinueForm = () => handleValidateEmail({ callback: () => handleGetTutorByEmail() })

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (watch.name) handleValidateName()

    if (Object.keys(errors).length > 0) return

    if (isEditing) {
      submitForm({ setTutor, setLoading, goToPage, isEditing, id, updated, setVisibility })
    } else {
      handleValidateEmail({ callback: () => submitForm({ setTutor, setLoading, goToPage, isEditing, id, updated, setVisibility }) })
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (isFirstStep) {
        handleContinueForm()
      } else if (!!watch.email && !isFirstStep) {
        handleSubmit(e)
      }
    }
  }

  return (
    <S.Form onSubmit={(e) => handleSubmit(e)} onKeyPress={(e) => handleKeyPress(e)} gap={isFirstStep && '14px'}>
      <S.ScrollWrapper>
        <S.InputWrapper>
          {(!isFirstStep) &&
            <S.StyledInput
              autoComplete='off'
              marginBottom='10px'
              label='Nome completo (obrigatório)'
              name='name'
              placeholder='Digite o nome do tutor'
              value={watch.name}
              errorMessage={errors?.name?.message}
              hookForm={register('name', {
                required: true
              })}
            />
          }
          <S.EmailWrapper disabled={isEditing}>
            <S.StyledInput
              autoComplete='off'
              marginBottom='10px'
              label='E-mail (obrigatório)'
              name='email'
              placeholder='Digite o e-mail do tutor'
              value={watch.email}
              errorMessage={errors?.email?.message}
              noShowErrorMessage={!isFirstStep && tutor?.id}
              onChange={(e) => {
                if (tutor?.id) setTutor()
                setValue('email', e.target.value)
              }}
            />
          </S.EmailWrapper>
        </S.InputWrapper>
        {(isEditing && !isFirstStep && email) &&
          <S.AlertEmail>
            <Alert show={isEditing} type='info'>
              Não é possível editar o e-mail por ser um dado identificador no sistema.
            </Alert>
          </S.AlertEmail>
        }
        {tutorWithoutEmail &&
          <S.AlertEmail>
            <Alert show={isEditing} type='warning'>
              Este tutor foi cadastrado sem o e-mail. Por favor, verifique o que pode ter acontecido diretamente com a recepção. Indicamos que, caso o registro esteja incorreto, inicie um novo cadastro.
            </Alert>
          </S.AlertEmail>
        }
        {(tutor?.email && !isFirstStep) &&
          <S.AlertEmail>
            <Alert show={tutor?.email} type='danger'>
              <strong>Atenção:</strong> O e-mail informado já está cadastrado no sistema e pertence a {tutor?.firstName}, {tutor?.document ? `de CPF ${formatCpf(tutor?.document)}` : 'o CPF não foi preenchido'}. Por favor, sugerimos que acesse a ficha já existente, ao invés de criar uma nova ficha com outro e-mail.
            </Alert>
          </S.AlertEmail>
        }
        {(!isFirstStep) &&
          <>
            <S.InputWrapper>
              <S.StyledInput
                autoComplete='off'
                marginBottom='10px'
                label='CPF'
                name='document'
                placeholder='Digite o CPF do tutor'
                pattern='\d{3}\.\d{3}\.\d{3}-\d{2}'
                mask='999.999.999-99'
                maskChar={null}
                value={watch.document || ''}
                errorMessage={errors?.document?.message}
                onChange={(e) => {
                  setValue('document', e.target.value.replace(/[^\w\s]/gi, ''))
                }}
              />
              <S.StyledInput
                autoComplete='off'
                marginBottom='10px'
                label='Telefone'
                name='homePhone'
                pattern='^\d{2} \d{4,5}-\d{4}$'
                placeholder='Digite o telefone com DDD'
                mask={!!watch.homePhone && watch.homePhone.replace(/\D/g, '').length === 10 ? '99 9999-99999' : '99 99999-9999'}
                maskChar={null}
                value={watch.homePhone || ''}
                errorMessage={errors?.homePhone?.message}
                onChange={(e) => {
                  setValue('homePhone', e.target.value)
                }}
              />
            </S.InputWrapper>
          </>
        }
        {(tutorByCpf && !isFirstStep) &&
          <S.AlertCpf>
            <Alert show={tutorByCpf} type='warning'>
              O CPF informado já possui um cadastro no sistema. Por favor, sugerimos que acesse a ficha já existente deste tutor, ao invés de criar uma nova ficha com outro e-mail.
            </Alert>
          </S.AlertCpf>
        }
      </S.ScrollWrapper>
      <S.Actions>
        <Button
          type='button'
          secondary
          color='#028BB7'
          onClick={() => {
            if (isFirstStep || isEditing) {
              setVisibility(false)
            } else {
              setIsFirstStep(true)
            }
          }}
        >
          {isFirstStep || isEditing ? 'Fechar' : 'Voltar'}
        </Button>
        {((!!watch.email || tutorWithoutEmail) && !isFirstStep)
          ? <S.SpinnerButton color='#028BB7' loading={loading}>
            <S.SpinnerButtonText>
              {isEditing
                ? 'Salvar alterações'
                : 'Salvar e cadastrar'
              }
            </S.SpinnerButtonText>
            <S.Spinner />
          </S.SpinnerButton>
          : <S.SpinnerButton type='button' color='#028BB7' onClick={handleContinueForm} loading={loading}>
            <S.SpinnerButtonText>Continuar</S.SpinnerButtonText>
            <S.Spinner />
          </S.SpinnerButton>}
      </S.Actions>
    </S.Form>
  )
}

export default NewTutorForm
